import type {
  SourceContext,
  SourceErrorCode,
  SourceRuntime,
  SourceState,
  SourceStatus,
  SourceStatusDetail,
  VideoFrameSource,
  VideoProvider,
} from '../core/definitions';
import type { SettingsBag } from '../core/model';
import type { Unsubscribe } from '../core/services';
import { FrameCounter, type FrameStats } from './frameStats';

/**
 * Shared plumbing for every built-in runtime: listener set, status, output
 * slots, idempotent destroy, and an "epoch" counter that lets async work detect
 * that it was superseded (settings changed) or the runtime was destroyed.
 */
export abstract class BaseSourceRuntime<S extends SettingsBag = SettingsBag> implements SourceRuntime<S> {
  protected destroyed = false;
  private readonly listeners = new Set<() => void>();
  private video: VideoProvider | null = null;
  private audio: AudioNode | null = null;
  private status: SourceStatus = { state: 'idle' };
  private epoch = 0;
  private version = 0;
  private readonly canvasFrames = new FrameCounter();

  constructor(protected readonly ctx: SourceContext) {}

  getVideo(): VideoProvider | null {
    return this.video;
  }

  getAudio(): AudioNode | null {
    return this.audio;
  }

  /** Pictures produced so far. Canvas-backed sources count redraws; element-backed ones override. */
  getFrameStats(): FrameStats {
    return this.canvasFrames.stats();
  }

  getStatus(): SourceStatus {
    return this.status;
  }

  subscribe(listener: () => void): Unsubscribe {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  abstract update(settings: S): void | Promise<void>;

  /** Release every resource this subclass acquired. Called exactly once. */
  protected abstract release(): void;

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    this.epoch++;
    try {
      this.release();
    } catch (err) {
      this.log('warn', 'release failed', err);
    }
    this.video = null;
    this.audio = null;
    this.listeners.clear();
  }

  /** Invalidates every earlier epoch and returns the new one. */
  protected beginEpoch(): number {
    return ++this.epoch;
  }

  protected isCurrent(epoch: number): boolean {
    return !this.destroyed && epoch === this.epoch;
  }

  protected setStatus(state: SourceState, error?: { code: SourceErrorCode; message: string }, detail?: SourceStatusDetail): void {
    const prev = this.status;
    if (prev.state === state && prev.detail === detail && prev.error?.code === error?.code && prev.error?.message === error?.message) return;
    const next: SourceStatus = error ? { state, error } : { state };
    if (detail) next.detail = detail;
    // A warning describes the current configuration and survives state changes until cleared.
    if (prev.warning) next.warning = prev.warning;
    this.status = next;
    this.emit();
  }

  /** Sets or clears the non-fatal `warning` of the status without changing its state. */
  protected setWarning(message: string | null): void {
    if ((this.status.warning ?? null) === message) return;
    const next: SourceStatus = { ...this.status };
    if (message) next.warning = message;
    else delete next.warning;
    this.status = next;
    this.emit();
  }

  protected fail(code: SourceErrorCode, message: string): void {
    this.setStatus('error', { code, message });
  }

  /** Replaces the provider; use `touchVideo` for in-place redraws of an unchanged surface. */
  protected publishVideo(
    source: VideoFrameSource,
    width: number,
    height: number,
    dynamic: boolean,
    /** Texture pixels per layout pixel when the source rasterised larger than its natural size. */
    pixelRatio = 1,
  ): void {
    this.video = { source, width, height, dynamic, version: ++this.version, ...(pixelRatio !== 1 ? { pixelRatio } : {}) };
    this.canvasFrames.note();
    this.emit();
  }

  protected publishCanvas(canvas: HTMLCanvasElement | OffscreenCanvas): void {
    this.publishVideo(canvas, canvas.width, canvas.height, false);
  }

  protected clearVideo(): void {
    if (!this.video) return;
    this.video = null;
    this.emit();
  }

  /** Marks the current surface as redrawn. No notification: the compositor polls `version` each frame. */
  protected touchVideo(): void {
    if (this.video) {
      this.video.version = ++this.version;
      this.canvasFrames.note();
    }
  }

  /** Swaps the picture inside an unchanged-size provider (frame ingestion): version bumps, no notification. */
  protected replaceSource(source: VideoFrameSource): void {
    if (!this.video) return;
    this.video.source = source;
    this.video.version = ++this.version;
  }

  protected setAudio(node: AudioNode | null): void {
    if (this.audio === node) return;
    this.audio = node;
    this.emit();
  }

  protected emit(): void {
    if (this.destroyed) return;
    for (const listener of [...this.listeners]) {
      try {
        listener();
      } catch (err) {
        this.log('warn', 'listener threw', err);
      }
    }
  }

  protected log(level: 'debug' | 'info' | 'warn' | 'error', message: string, detail?: unknown): void {
    this.ctx.logger.log(level, `source:${this.ctx.sourceId}`, message, detail);
  }
}

export function num(value: unknown, fallback: number, min = -Infinity, max = Infinity): number {
  const n = typeof value === 'number' ? value : Number.NaN;
  if (!Number.isFinite(n)) return fallback;
  return Math.min(max, Math.max(min, n));
}

export function str(value: unknown, fallback: string): string {
  return typeof value === 'string' ? value : fallback;
}

export function bool(value: unknown, fallback: boolean): boolean {
  return typeof value === 'boolean' ? value : fallback;
}
